import type { Alert } from '@/types/alert'
import { formatDateTime } from '@/lib/format'

const SEVERITY_STYLE: Record<string, { dot: string; label: string; text: string }> = {
  info: { dot: 'bg-faint', label: 'INFO', text: 'text-muted' },
  warning: { dot: 'bg-bernstein', label: 'WARN', text: 'text-bernstein' },
  critical: { dot: 'bg-loss', label: 'CRIT', text: 'text-loss' },
}

const CATEGORY_LABEL: Record<string, string> = {
  stop_loss: 'Stop-Loss',
  rebalance: 'Rebalancing',
  profit_taking: 'Profit-Taking',
  concentration: 'Konzentration',
  tax: 'Steuer',
  asset_check: 'Asset-Check',
  strategy: 'Strategie',
  import: 'Import',
  system: 'System',
}

interface AlertItemProps {
  alert: Alert
  onClick?: (alert: Alert) => void
  onOrderTicket?: (alert: Alert) => void
  onRebalance?: (alert: Alert) => void
  onAccept?: (alert: Alert) => void
  compact?: boolean
}

export function AlertItem({
  alert,
  onClick,
  onOrderTicket,
  onRebalance,
  onAccept,
  compact = false,
}: AlertItemProps) {
  const style = SEVERITY_STYLE[alert.severity] ?? SEVERITY_STYLE.info
  const category = CATEGORY_LABEL[alert.category] ?? alert.category
  const isUnread = alert.read_at === null
  // Order-Ticket-Button bei Hard-Rule-Verletzungen (Stop-Triggered ODER
  // Konzentration > 20 %) — beide kommen als severity=critical mit
  // related_position_id durch.
  const showTicketButton =
    onOrderTicket !== undefined &&
    alert.severity === 'critical' &&
    (alert.category === 'stop_loss' || alert.category === 'profit_taking') &&
    alert.related_position_id !== null

  // Concentration-Alerts (eigene Kategorie) tragen die 3 Handlungsoptionen.
  // Position-Alert (related_position_id gesetzt) → Teilverkauf möglich;
  // Cluster-Alert (null) → nur Rebalancing + Akzeptieren.
  const isConcentration = alert.category === 'concentration'
  const isActioned = alert.actioned_at !== null
  const showConcentrationActions = isConcentration && !compact
  const showSell = alert.related_position_id !== null && onOrderTicket !== undefined

  const Wrapper = onClick ? 'button' : 'div'

  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      aria-label={onClick ? alert.title : undefined}
      onClick={onClick ? () => onClick(alert) : undefined}
      className={[
        'hairline-b flex w-full items-start gap-4 py-4 text-left transition-colors',
        onClick ? 'hover:bg-void/40' : '',
        isUnread ? '' : 'opacity-60',
      ].join(' ')}
    >
      <span className={`mt-2 size-1.5 shrink-0 rounded-full ${style.dot}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className={`tracked-caps text-[10px] ${style.text}`}>{style.label}</span>
          <span className="font-mono text-[10px] text-faint">{category}</span>
          <span className="ml-auto font-mono text-[10px] text-faint">
            {formatDateTime(alert.created_at)}
          </span>
        </div>
        <p className="mt-1.5 truncate text-sm text-ink">{alert.title}</p>
        {!compact && (
          <p className="mt-1 line-clamp-2 break-words font-mono text-xs text-muted">{alert.body}</p>
        )}
        {!compact && showTicketButton && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              onOrderTicket?.(alert)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                onOrderTicket?.(alert)
              }
            }}
            className="tracked-caps mt-3 inline-block border border-bernstein px-3 py-1.5 text-[10px] text-bernstein transition-colors hover:bg-bernstein hover:text-void"
          >
            Order-Ticket
          </span>
        )}
        {showConcentrationActions && isActioned && (
          <p className="mt-3 font-mono text-[10px] text-faint">✓ Akzeptiert</p>
        )}
        {showConcentrationActions && !isActioned && (
          <div className="mt-3 flex flex-wrap gap-2">
            {showSell && (
              <ActionButton
                label="Teilverkauf · Order-Ticket"
                className="border-loss text-loss hover:bg-loss hover:text-void"
                onActivate={() => onOrderTicket?.(alert)}
              />
            )}
            <ActionButton
              label="Rebalancing öffnen"
              className="border-bernstein text-bernstein hover:bg-bernstein hover:text-void"
              onActivate={() => onRebalance?.(alert)}
            />
            <ActionButton
              label="Akzeptieren"
              className="border-faint text-muted hover:bg-faint hover:text-void"
              onActivate={() => onAccept?.(alert)}
            />
          </div>
        )}
      </div>
      {alert.telegram_sent && (
        <span
          className="shrink-0 font-mono text-[10px] text-faint"
          title="Telegram zugestellt"
          aria-label="Telegram zugestellt"
        >
          ↗
        </span>
      )}
    </Wrapper>
  )
}

interface ActionButtonProps {
  label: string
  className: string
  onActivate: () => void
}

function ActionButton({ label, className, onActivate }: ActionButtonProps) {
  return (
    <span
      role="button"
      aria-label={label}
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation()
        onActivate()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.stopPropagation()
          onActivate()
        }
      }}
      className={`tracked-caps inline-block border px-3 py-1.5 text-[10px] transition-colors ${className}`}
    >
      {label}
    </span>
  )
}
